import React from 'react';
import { View, Text } from '@react-pdf/renderer';
import { commonPDFStyles } from '../styles/pdfStyles';
import { typographyStyles, specializedTypography } from '../styles/typography';
import { createT, hasTenantOverride } from '../utils/simpleTranslate';
import { formatBirthdateForPDF, formatDateForPDF } from '../utils/dataFormatters';
import { yearsFromDate, getHealthIDLabel, getPatientID, birthdateToDate } from '../../../utils';

/**
 * PDF Patient Info Component
 * 
 * Replaces PatientInfoSection component with @react-pdf/renderer equivalent.
 * Displays patient information in a single column layout.
 */

interface IPatient {
  id?: string;
  personalData?: {
    name?: string;
    surnames?: string;
    birthdate?: string | Date;
    sex?: string;
    phone?: string;
    insurance?: string | number;
    healthId?: string;
    health_id?: string;
    automated_health_id?: string;
    nationalId?: string;
    passportNumber?: string;
  };
}

interface PDFPatientInfoProps {
  patient: IPatient;
  locale?: string;
  patientCoverage?: { name: string } | null;
  /**
   * Number of columns to lay the fields out in (SHER-425). Defaults to 1
   * (single vertical column) so every existing caller is unchanged; the
   * Synthèse des actes PDF opts into 2 to keep the header compact.
   */
  columns?: number;
  translations?: {
    name?: string;
    birthdate?: string;
    age?: string;
    sex?: string;
    healthId?: string;
    insurance?: string;
    coverage?: string;
    phone?: string;
  };
}

export const PDFPatientInfo: React.FC<PDFPatientInfoProps> = ({
  patient,
  locale = 'fr',
  patientCoverage,
  columns = 1,
  translations,
}) => {
  const t = createT(locale);

  // SHER-425: with columns=2 the fields wrap into a 2-column grid (each field 50% wide)
  // so a ~6-field block is ~3 rows tall instead of 6. columns=1 keeps the original
  // single vertical column, so all other callers render exactly as before.
  const twoColumn = columns === 2;
  const fieldStyle = twoColumn ? { marginBottom: 3, width: '50%', paddingRight: 6 } : { marginBottom: 3 };
  // The name is the widest/primary field — give it its own full-width row so it never
  // wraps inside a half-width cell and the remaining fields pair up cleanly beneath it.
  const nameFieldStyle = twoColumn ? { marginBottom: 3, width: '100%' } : { marginBottom: 3 };

  // Use translations from props or defaults
  const labels = {
    name: translations?.name || t('general.full_name'),
    birthdate: translations?.birthdate || t('investigation.create.personal_data.fields.birthdate'),
    age: translations?.age || t('general.age'),
    sex: translations?.sex || t('investigation.create.personal_data.fields.sex'),
    coverage: translations?.coverage || t('investigation.create.personal_data.fields.coverage'),
    phone: translations?.phone || t('investigation.create.personal_data.fields.phone'),
  };
  
  // Get health ID label dynamically based on patient data
  const healthIdLabel = translations?.healthId || t(getHealthIDLabel(patient));

  if (!patient?.personalData) {
    return (
      <Text style={specializedTypography.statusInactive}>
        {t('patient.info_not_available')}
      </Text>
    );
  }

  const { personalData } = patient;

  return (
    <View style={twoColumn ? { width: '100%', flexDirection: 'row', flexWrap: 'wrap' } : { width: '100%' }}>
      {/* Patient Name */}
      <View style={nameFieldStyle}>
        <Text>
          <Text style={specializedTypography.fieldLabel}>
            {labels.name}:
          </Text>
          <Text style={specializedTypography.patientName}>
            {' '}{personalData.name} {personalData.surnames}
          </Text>
        </Text>
      </View>

      {/* Birthdate */}
      {personalData.birthdate && (
        <View style={fieldStyle}>
          <Text>
            <Text style={specializedTypography.fieldLabel}>
              {labels.birthdate}:
            </Text>
            <Text style={specializedTypography.fieldValue}>
              {' '}{formatBirthdateForPDF(personalData.birthdate, locale)}
            </Text>
          </Text>
        </View>
      )}

      {/* Sex */}
      {personalData.sex && (
        <View style={fieldStyle}>
          <Text>
            <Text style={specializedTypography.fieldLabel}>
              {labels.sex}:
            </Text>
            <Text style={specializedTypography.fieldValue}>
              {' '}{personalData.sex === "Male" ? t('general.male') : t('general.female')}
            </Text>
          </Text>
        </View>
      )}

      {/* Health ID */}
      <View style={fieldStyle}>
        <Text>
          <Text style={specializedTypography.fieldLabel}>
            {healthIdLabel}:
          </Text>
          <Text style={specializedTypography.patientId}>
            {' '}{getPatientID(patient)}
          </Text>
        </Text>
      </View>

      {/* Dossier number (SHER-371). Shown IN ADDITION to the NDM line above, and
          ONLY for tenants whose literals overlay gives `health_id` its own name
          (e.g. CHOM Dakar's "Dossier CHOM") — `hasTenantOverride` keeps this scoped
          to those tenants via the tenant file, with no hard-coded slug here.
          Gated on both IDs existing: getPatientID/getHealthIDLabel above resolve to
          the NDM when present, so a patient with no NDM already has their dossier
          rendered by that block and a second line here would duplicate it. */}
      {personalData.automated_health_id &&
        personalData.health_id &&
        hasTenantOverride('investigation.create.personal_data.fields.health_id') && (
        <View style={fieldStyle}>
          <Text>
            <Text style={specializedTypography.fieldLabel}>
              {t('investigation.create.personal_data.fields.health_id')}:
            </Text>
            <Text style={specializedTypography.patientId}>
              {' '}{personalData.health_id}
            </Text>
          </Text>
        </View>
      )}

      {/* Coverage */}
      {patientCoverage && (
        <View style={fieldStyle}>
          <Text>
            <Text style={specializedTypography.fieldLabel}>
              {labels.coverage}:
            </Text>
            <Text style={specializedTypography.fieldValue}>
              {' '}{patientCoverage.name}
            </Text>
          </Text>
        </View>
      )}



      {/* Age */}
      {personalData.birthdate && (
        <View style={fieldStyle}>
          <Text>
            <Text style={specializedTypography.fieldLabel}>
              {labels.age}:
            </Text>
            <Text style={specializedTypography.fieldValue}>
              {' '}{personalData.birthdate ? yearsFromDate(birthdateToDate(personalData.birthdate)) : ''} {t('general.years')}
            </Text>
          </Text>
        </View>
      )}
    </View>
  );
};

export default PDFPatientInfo;
